/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  ExpandLess,
  ExpandMore,
  CheckCircleOutline,
  LightbulbOutlined,
  WarningAmberOutlined
} from '@mui/icons-material';

const SignalRow = ({ signal, tone, forceOpen, compact = false }) => {
  const [open, setOpen] = useState(!compact);
  useEffect(() => {
    if (typeof forceOpen === 'boolean') {
      setOpen(forceOpen);
    }
  }, [forceOpen]);
  const isStrength = tone === 'strength';
  const isOpportunity = tone === 'opportunity';
  const Icon = isStrength
    ? CheckCircleOutline
    : isOpportunity
      ? LightbulbOutlined
      : WarningAmberOutlined;
  const iconColor = isStrength
    ? 'text-emerald-500'
    : isOpportunity
      ? 'text-sky-500'
      : 'text-amber-500';
  const borderColor = isStrength
    ? 'border-emerald-200 dark:border-emerald-900/80'
    : isOpportunity
      ? 'border-sky-200 dark:border-sky-900/80'
      : 'border-amber-200 dark:border-amber-900/80';
  const surface = isStrength
    ? 'bg-emerald-50/60 dark:bg-emerald-950/20'
    : isOpportunity
      ? 'bg-sky-50/60 dark:bg-sky-950/20'
      : 'bg-amber-50/70 dark:bg-amber-950/20';
  return (
    <div
      className={`${compact ? 'rounded-xl' : 'rounded-2xl bg-white shadow-sm hover:shadow-md dark:bg-boxdark'} overflow-hidden border transition ${borderColor} ${compact ? surface : ''}`}
    >
      <button
        type="button"
        className={`${compact ? 'p-3' : 'border-b border-slate-100 px-5 py-4 dark:border-strokedark'} flex w-full items-center gap-3 text-left`}
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
      >
        <span
          className={
            compact
              ? ''
              : `flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${surface}`
          }
        >
          <Icon className={iconColor} fontSize="small" />
        </span>
        <span
          className={`${compact ? 'text-sm' : 'text-base'} flex-1 font-bold text-slate-900 dark:text-slate-100`}
        >
          {signal.title}
        </span>
        <span
          className={`hidden rounded-lg border px-3 py-1 text-xs font-bold sm:inline-flex ${compact ? 'sm:hidden' : ''} ${borderColor} ${iconColor}`}
        >
          {isStrength
            ? 'Positive multiplier'
            : isOpportunity
              ? 'Growth opportunity'
              : 'Review risk'}
        </span>
        {open ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
      </button>
      {open ? (
        <div
          className={`${compact ? 'border-t border-inherit px-4 py-3' : 'px-5 py-4'} space-y-3 text-sm leading-6 text-slate-600 dark:text-slate-300`}
        >
          <div
            className={
              compact ? '' : `rounded-xl border p-4 ${surface} ${borderColor}`
            }
          >
            <p className="mb-1 flex items-center gap-2 font-bold text-slate-800 dark:text-slate-100">
              <Icon className={iconColor} fontSize="small" />
              What we observed
            </p>
            <p>{signal.observation}</p>
          </div>
          <div
            className={
              compact
                ? ''
                : 'rounded-xl border border-slate-200 p-4 dark:border-strokedark'
            }
          >
            <p className="mb-1 font-bold text-slate-800 dark:text-slate-100">
              Why it matters
            </p>
            <p>{signal.why_it_matters}</p>
          </div>
          {signal.recommended_action ? (
            <div
              className={
                compact
                  ? ''
                  : 'rounded-xl border border-amber-300 bg-amber-50/70 p-4 text-amber-950 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-100'
              }
            >
              <p className="mb-1 font-bold">
                {isOpportunity ? 'How to improve' : 'Recommended action'}
              </p>
              <p>{signal.recommended_action}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const SignalInsights = ({ signals = {}, compact = false }) => {
  const [showAllStrengths, setShowAllStrengths] = useState(false);
  const [showAllWatchouts, setShowAllWatchouts] = useState(false);
  const [showOpportunities, setShowOpportunities] = useState(false);
  const [forceOpen, setForceOpen] = useState(true);
  const allStrengths = signals.strengths || [];
  const allWatchouts = signals.watchouts || [];
  const allOpportunities = signals.opportunities || [];
  const initialSignalCount = compact ? 2 : 3;
  const strengths = compact
    ? showAllStrengths
      ? allStrengths
      : allStrengths.slice(0, initialSignalCount)
    : allStrengths;
  const watchouts = compact
    ? showAllWatchouts
      ? allWatchouts
      : allWatchouts.slice(0, initialSignalCount)
    : allWatchouts;
  const hiddenStrengthCount = Math.max(
    0,
    allStrengths.length - initialSignalCount
  );
  const hiddenWatchoutCount = Math.max(
    0,
    allWatchouts.length - initialSignalCount
  );
  const hasOpportunities = allOpportunities.length > 0;
  const findingCount =
    allStrengths.length + allWatchouts.length + allOpportunities.length;
  const containerClass = compact
    ? 'space-y-3'
    : `grid gap-5 ${hasOpportunities ? 'xl:grid-cols-3' : 'lg:grid-cols-2'}`;

  return (
    <div className={compact ? containerClass : 'space-y-5'}>
      {!compact ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4 dark:border-strokedark">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Showing{' '}
              <span className="font-bold text-slate-800 dark:text-slate-100">
                {findingCount}
              </span>{' '}
              organizational findings
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForceOpen(value => !value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 shadow-sm transition hover:border-primary hover:text-primary dark:border-strokedark dark:bg-boxdark dark:text-slate-300"
              >
                {forceOpen ? '↙ Collapse all' : '↗ Expand all'}
              </button>
            </div>
          </div>
          <div className="grid gap-8 lg:grid-cols-2">
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-3 text-xl font-extrabold text-slate-900 dark:text-white">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                    <CheckCircleOutline />
                  </span>
                  Strengths{' '}
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                    {allStrengths.length}
                  </span>
                </h3>
                <span className="hidden text-xs font-semibold text-slate-400 sm:block">
                  Positive multipliers
                </span>
              </div>
              <div className="space-y-4">
                {strengths.length ? (
                  strengths.map(signal => (
                    <SignalRow
                      key={signal.id}
                      signal={signal}
                      tone="strength"
                      forceOpen={forceOpen}
                    />
                  ))
                ) : (
                  <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-800">
                    No strong positive signal is available for this period.
                  </p>
                )}
              </div>
            </section>
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-3 text-xl font-extrabold text-slate-900 dark:text-white">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
                    <WarningAmberOutlined />
                  </span>
                  Watchouts{' '}
                  <span className="rounded-full bg-amber-100 px-2.5 py-1 text-sm text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                    {allWatchouts.length}
                  </span>
                </h3>
                <span className="hidden text-xs font-semibold text-slate-400 sm:block">
                  Attention &amp; mitigation areas
                </span>
              </div>
              <div className="space-y-4">
                {watchouts.length ? (
                  watchouts.map(signal => (
                    <SignalRow
                      key={signal.id}
                      signal={signal}
                      tone="watchout"
                      forceOpen={forceOpen}
                    />
                  ))
                ) : (
                  <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-800">
                    No material watchout is detected for this period.
                  </p>
                )}
              </div>
            </section>
          </div>
        </>
      ) : null}
      {compact ? (
        <>
          <section>
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
              Strengths
            </h3>
            <div className="space-y-2">
              {strengths.length ? (
                strengths.map(signal => (
                  <SignalRow
                    key={signal.id}
                    signal={signal}
                    tone="strength"
                    compact
                  />
                ))
              ) : (
                <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  No strong positive signal is available for this period.
                </p>
              )}
              {compact && hiddenStrengthCount ? (
                <button
                  type="button"
                  className="w-full rounded-lg border border-dashed border-emerald-300 px-3 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
                  onClick={() => setShowAllStrengths(value => !value)}
                  aria-expanded={showAllStrengths}
                >
                  {showAllStrengths
                    ? 'Show fewer strengths'
                    : `+${hiddenStrengthCount} more strength${hiddenStrengthCount === 1 ? '' : 's'}`}
                </button>
              ) : null}
            </div>
          </section>
          <section>
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              Watchouts
            </h3>
            <div className="space-y-2">
              {watchouts.length ? (
                watchouts.map(signal => (
                  <SignalRow
                    key={signal.id}
                    signal={signal}
                    tone="watchout"
                    compact
                  />
                ))
              ) : (
                <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  No material watchout is detected for this period.
                </p>
              )}
              {compact && hiddenWatchoutCount ? (
                <button
                  type="button"
                  className="w-full rounded-lg border border-dashed border-amber-300 px-3 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-950/30"
                  onClick={() => setShowAllWatchouts(value => !value)}
                  aria-expanded={showAllWatchouts}
                >
                  {showAllWatchouts
                    ? 'Show fewer watchouts'
                    : `+${hiddenWatchoutCount} more watchout${hiddenWatchoutCount === 1 ? '' : 's'}`}
                </button>
              ) : null}
            </div>
          </section>
          {hasOpportunities ? (
            <section>
              {compact ? (
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-xl border border-dashed border-sky-300 px-3 py-3 text-left text-sm font-bold uppercase tracking-wide text-sky-700 transition hover:bg-sky-50 dark:border-sky-800 dark:text-sky-400 dark:hover:bg-sky-950/30"
                  onClick={() => setShowOpportunities(value => !value)}
                  aria-expanded={showOpportunities}
                >
                  <span>Growth opportunities ({allOpportunities.length})</span>
                  {showOpportunities ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </button>
              ) : (
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-sky-700 dark:text-sky-400">
                  Growth opportunities
                </h3>
              )}
              {!compact || showOpportunities ? (
                <div className={compact ? 'mt-2 space-y-2' : 'space-y-2'}>
                  {allOpportunities.map(signal => (
                    <SignalRow
                      key={signal.id}
                      signal={signal}
                      tone="opportunity"
                      compact
                    />
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default SignalInsights;
